import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Buildings, IdentificationBadge, Lock, LockOpen, PencilSimple, Plus } from 'phosphor-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { IUser } from '../../hooks/useUsers';
import { useDialog } from '../../providers/dialog';
import { useAuth } from '../../providers/auth';
import { acessoApi, cnpjFormatado, iniciais } from './acessoApi';
import { UltimosAcessos } from './LogsDeAcesso';
import {
  AZUL,
  AZUL_ESCURO,
  Busca,
  Cartao,
  Contador,
  Etiqueta,
  Eyebrow,
  MONO,
  Pilula,
  TEXTO,
  TEXTO_SUAVE,
} from './ui';

type Situacao = 'todos' | 'ativos' | 'inativos';
const ATIVO: Record<Situacao, boolean | undefined> = {
  todos: undefined,
  ativos: true,
  inativos: false,
};

const TELA = '/configuracoes/clientes';
function useAcoesDeClientes() {
  const { isAdmin, hasPermission } = useAuth();
  return {
    criar: isAdmin() || hasPermission(TELA, 'create'),
    editar: isAdmin() || hasPermission(TELA, 'edit'),
  };
}

const Avatar = ({
  nome,
  ativo,
  tamanho = 36,
}: {
  nome: string;
  ativo: boolean;
  tamanho?: number;
}) => (
  <Flex
    w={`${tamanho}px`}
    h={`${tamanho}px`}
    borderRadius="50%"
    align="center"
    justify="center"
    flex="none"
    fontSize={tamanho > 40 ? '17px' : '12px'}
    fontWeight={700}
    bg={ativo ? '#DCFCE7' : '#F1F5F9'}
    color={ativo ? '#166534' : '#64748B'}
  >
    {iniciais(nome)}
  </Flex>
);

function PainelDoCliente({ cliente }: { cliente: IUser }) {
  const navigate = useNavigate();
  const pode = useAcoesDeClientes();
  const showDialog = useDialog();
  const queryClient = useQueryClient();
  const { data: detalhe, isLoading } = useQuery({
    queryKey: ['central', 'cliente', cliente.id],
    queryFn: () => acessoApi.cliente(cliente.id as number),
  });

  const alternar = useMutation({
    mutationFn: () => acessoApi.alterarAtivoDoCliente(cliente, !cliente.ativo),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['central', 'clientes'] });
      await queryClient.invalidateQueries({ queryKey: ['central', 'cliente', cliente.id] });
      toast.success(cliente.ativo ? 'Cliente desativado' : 'Cliente ativado');
    },
    onError: () => toast.error('Não foi possível alterar o cliente'),
  });

  const empresas = detalhe?.empresas ?? [];
  const [buscaEmpresa, setBuscaEmpresa] = useState('');
  const [todasEmpresas, setTodasEmpresas] = useState(false);
  const termo = buscaEmpresa.trim().toLowerCase();
  const digitos = termo.replace(/\D/g, '');
  const empresasFiltradas = empresas.filter(
    e =>
      !termo ||
      `${e.razao_social} ${e.nome_fantasia}`.toLowerCase().includes(termo) ||
      (digitos.length >= 3 && (e.cnpj ?? '').replace(/\D/g, '').includes(digitos)),
  );
  const empresasVisiveis =
    todasEmpresas || termo ? empresasFiltradas : empresasFiltradas.slice(0, 8);

  return (
    <Flex direction="column" gap="16px">
      <Cartao p="20px 22px" display="flex" flexDirection="column" gap="16px">
        <Flex align="center" gap="14px" wrap="wrap">
          <Avatar nome={cliente.nome} ativo={cliente.ativo} tamanho={52} />
          <Box flex="1 1 240px" minW={0}>
            <Eyebrow>CLIENTE DA ÁREA VIP</Eyebrow>
            <Text
              fontSize="22px"
              lineHeight="28px"
              fontWeight={700}
              letterSpacing="-.3px"
              noOfLines={1}
            >
              {cliente.nome}
            </Text>
            <Text fontSize="13px" color="#475569" noOfLines={1}>
              {cliente.email}
            </Text>
          </Box>
          {pode.editar && (
            <Flex gap="8px" wrap="wrap">
              <Button
                size="sm"
                variant="outline"
                borderRadius="999px"
                bg="rgba(255,255,255,.7)"
                leftIcon={<PencilSimple size={14} />}
                onClick={() => navigate(`/configuracoes/clientes/${cliente.id}/editar`)}
              >
                Editar cadastro
              </Button>
              <Button
                size="sm"
                variant="outline"
                borderRadius="999px"
                bg="rgba(255,255,255,.7)"
                color={cliente.ativo ? '#991B1B' : '#166534'}
                leftIcon={cliente.ativo ? <Lock size={14} /> : <LockOpen size={14} />}
                isLoading={alternar.isPending}
                onClick={() =>
                  showDialog({
                    status: 'info',
                    title: cliente.ativo ? 'Desativar cliente' : 'Ativar cliente',
                    description: cliente.ativo
                      ? `${cliente.nome} deixa de entrar na Área VIP.`
                      : `${cliente.nome} volta a entrar na Área VIP.`,
                    async onConfirm() {
                      await alternar.mutateAsync();
                    },
                  })
                }
              >
                {cliente.ativo ? 'Desativar' : 'Ativar'}
              </Button>
            </Flex>
          )}
        </Flex>
        <Flex gap="10px" wrap="wrap">
          {[
            ['SITUAÇÃO', cliente.ativo ? 'Ativo' : 'Inativo'],
            ['ID', String(cliente.id)],
            ['CPF', detalhe?.cpf || '—'],
            ['EMPRESAS', isLoading ? '…' : String(empresas.length)],
          ].map(([rotulo, valor]) => (
            <Box
              key={rotulo}
              flex="1 1 120px"
              p="10px 14px"
              borderRadius="14px"
              bg="rgba(248,250,252,.8)"
              boxShadow="inset 0 0 0 1px rgba(15,23,42,.05)"
            >
              <Eyebrow>{rotulo}</Eyebrow>
              <Text
                fontSize="15px"
                fontWeight={700}
                color={TEXTO}
                fontFamily={rotulo === 'ID' || rotulo === 'CPF' ? MONO : undefined}
              >
                {valor}
              </Text>
            </Box>
          ))}
        </Flex>
      </Cartao>

      <Cartao p="18px 22px" display="flex" flexDirection="column" gap="10px">
        <Flex justify="space-between" align="center">
          <Eyebrow>EMPRESAS QUE ELE VÊ NA ÁREA VIP</Eyebrow>
          <Contador>{empresas.length}</Contador>
        </Flex>
        {isLoading && <Spinner size="sm" color={AZUL} />}
        {!isLoading && empresas.length === 0 && (
          <Text fontSize="13px" color={TEXTO_SUAVE}>
            Nenhuma empresa vinculada. Vincule em &quot;Editar cadastro&quot;.
          </Text>
        )}
        {empresas.length > 8 && (
          <Box maxW="420px">
            <Busca
              valor={buscaEmpresa}
              onChange={setBuscaEmpresa}
              placeholder="Buscar empresa ou CNPJ"
            />
          </Box>
        )}
        {termo && empresasFiltradas.length === 0 && (
          <Text fontSize="13px" color={TEXTO_SUAVE}>
            Nenhuma empresa dele com esse nome ou CNPJ.
          </Text>
        )}
        {empresasVisiveis.map(e => (
          <Flex
            key={e.id}
            align="center"
            gap="12px"
            p="10px 12px"
            borderRadius="14px"
            bg="white"
            boxShadow="inset 0 0 0 1px rgba(15,23,42,.06)"
            wrap="wrap"
          >
            <Buildings size={18} color={TEXTO_SUAVE} />
            <Box flex="1 1 220px" minW={0}>
              <Text fontSize="13.5px" fontWeight={600} noOfLines={1}>
                {e.nome_fantasia || e.razao_social}
              </Text>
              <Text fontSize="12px" color={TEXTO_SUAVE} noOfLines={1}>
                {e.nome_fantasia && e.nome_fantasia !== e.razao_social
                  ? `${e.razao_social} · `
                  : ''}
                <Box as="span" fontFamily={MONO}>
                  {cnpjFormatado(e.cnpj)}
                </Box>
              </Text>
            </Box>
            <Etiqueta tom={e.validado ? 'sucesso' : 'aviso'}>
              {e.validado ? 'validada' : 'aguardando validação'}
            </Etiqueta>
          </Flex>
        ))}
        {!termo && empresas.length > 8 && (
          <Button
            alignSelf="flex-start"
            size="sm"
            variant="ghost"
            borderRadius="999px"
            onClick={() => setTodasEmpresas(v => !v)}
          >
            {todasEmpresas ? 'Mostrar menos' : `Mostrar todas as ${empresas.length}`}
          </Button>
        )}
      </Cartao>

      <Cartao p="18px 22px">
        <UltimosAcessos usuarioId={cliente.id as number} nome={cliente.nome} />
      </Cartao>
    </Flex>
  );
}

/** Aba "Clientes": quem entra na Área VIP e quais empresas cada um vê. */
export function ClientesCentral() {
  const navigate = useNavigate();
  const pode = useAcoesDeClientes();
  const [params, setParams] = useSearchParams();
  const selecionadoId = Number(params.get('cliente')) || null;
  const [situacao, setSituacao] = useState<Situacao>('todos');
  const [digitado, setDigitado] = useState('');
  const [busca, setBusca] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBusca(digitado.trim()), 350);
    return () => clearTimeout(t);
  }, [digitado]);

  const lista = useInfiniteQuery({
    queryKey: ['central', 'clientes', 'lista', { busca, situacao }],
    queryFn: ({ pageParam }) =>
      acessoApi.clientes({ texto: busca, ativo: ATIVO[situacao] }, pageParam),
    initialPageParam: 1,
    getNextPageParam: ultima => ultima.next_page ?? undefined,
  });
  const clientes = useMemo(() => (lista.data?.pages ?? []).flatMap(p => p.items), [lista.data]);

  // contagens de verdade (a tela antiga contava só a página aberta)
  const { data: totais } = useQuery({
    queryKey: ['central', 'clientes', 'totais'],
    queryFn: async () => {
      const [todos, ativos, inativos] = await Promise.all([
        acessoApi.totalDeClientes(),
        acessoApi.totalDeClientes(true),
        acessoApi.totalDeClientes(false),
      ]);
      return { todos, ativos, inativos };
    },
  });

  // aberto por link e fora da página carregada
  const naLista = clientes.find(c => c.id === selecionadoId);
  const { data: porId } = useQuery({
    queryKey: ['central', 'clientes', 'por-id', selecionadoId],
    queryFn: async () =>
      (await acessoApi.clientes({ texto: String(selecionadoId) }, 1, 1)).items[0],
    enabled: !!selecionadoId && !naLista && !lista.isLoading,
  });
  const selecionado = naLista ?? porId ?? null;

  const escolher = (id: number | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set('cliente', String(id));
    else p.delete('cliente');
    setParams(p, { replace: true });
  };

  return (
    <Flex gap="20px" align="flex-start" wrap="wrap">
      <Cartao
        as="section"
        aria-label="Clientes"
        flex="1 1 280px"
        maxW={{ base: '100%', lg: '340px' }}
        minW={0}
        p="18px"
        display="flex"
        flexDirection="column"
        gap="12px"
      >
        <Flex align="center" justify="space-between" gap="8px">
          <Eyebrow>CLIENTES</Eyebrow>
          {pode.criar && (
            <Button
              size="xs"
              borderRadius="999px"
              variant="outline"
              bg="white"
              leftIcon={<Plus size={12} />}
              onClick={() => navigate('/configuracoes/clientes/criar')}
            >
              Novo cliente
            </Button>
          )}
        </Flex>
        <Busca valor={digitado} onChange={setDigitado} placeholder="Nome, e-mail ou ID" />
        <Flex gap="6px" wrap="wrap">
          <Pilula
            ativa={situacao === 'todos'}
            onClick={() => setSituacao('todos')}
            contagem={totais?.todos}
          >
            Todos
          </Pilula>
          <Pilula
            ativa={situacao === 'ativos'}
            onClick={() => setSituacao('ativos')}
            contagem={totais?.ativos}
          >
            Ativos
          </Pilula>
          <Pilula
            ativa={situacao === 'inativos'}
            onClick={() => setSituacao('inativos')}
            contagem={totais?.inativos}
          >
            Inativos
          </Pilula>
        </Flex>
        <Flex direction="column" gap="4px" maxH="560px" overflowY="auto" mx="-6px" px="6px">
          {lista.isLoading && (
            <Flex justify="center" py={6}>
              <Spinner color={AZUL} />
            </Flex>
          )}
          {!lista.isLoading && clientes.length === 0 && (
            <Text fontSize="13px" color={TEXTO_SUAVE} py="8px">
              Nenhum cliente com esse filtro.
            </Text>
          )}
          {clientes.map(c => {
            const ativo = c.id === selecionado?.id;
            return (
              <Flex
                key={c.id}
                as="button"
                type="button"
                onClick={() => escolher(c.id as number)}
                aria-current={ativo ? 'true' : undefined}
                align="center"
                gap="10px"
                p="8px 10px"
                borderRadius="14px"
                textAlign="left"
                bg={ativo ? '#DFF3FF' : 'transparent'}
                boxShadow={ativo ? `inset 0 0 0 1px ${AZUL}33` : undefined}
                _hover={ativo ? undefined : { bg: 'rgba(255,255,255,.8)' }}
              >
                <Avatar nome={c.nome} ativo={c.ativo} />
                <Box flex={1} minW={0}>
                  <Text fontSize="13.5px" fontWeight={600} color={TEXTO} noOfLines={1}>
                    {c.nome}
                  </Text>
                  <Text fontSize="12px" color={TEXTO_SUAVE} noOfLines={1}>
                    {c.email}
                  </Text>
                </Box>
                {!c.ativo && <Etiqueta tom="neutro">inativo</Etiqueta>}
              </Flex>
            );
          })}
          {lista.hasNextPage && (
            <Button
              size="sm"
              variant="ghost"
              borderRadius="999px"
              isLoading={lista.isFetchingNextPage}
              onClick={() => lista.fetchNextPage()}
            >
              Carregar mais
            </Button>
          )}
        </Flex>
      </Cartao>

      <Box flex="999 1 520px" minW={0}>
        {selecionado ? (
          <PainelDoCliente key={selecionado.id} cliente={selecionado} />
        ) : (
          <Cartao
            p="28px"
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="10px"
            textAlign="center"
          >
            <Flex
              w="52px"
              h="52px"
              borderRadius="16px"
              bg="#DCFCE7"
              color="#166534"
              align="center"
              justify="center"
            >
              <IdentificationBadge size={26} weight="duotone" />
            </Flex>
            <Text fontSize="16px" fontWeight={700} color={TEXTO}>
              Escolha um cliente
            </Text>
            <Text fontSize="13px" color={TEXTO_SUAVE} maxW="440px">
              Clientes entram na Área VIP e veem só as empresas vinculadas a eles. Aqui você vê
              essas empresas, os últimos acessos e ativa ou desativa o cliente.
            </Text>
            {pode.criar && (
              <Button
                mt="6px"
                size="sm"
                borderRadius="999px"
                color="white"
                bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
                _hover={{ bg: AZUL_ESCURO }}
                leftIcon={<Plus size={14} />}
                onClick={() => navigate('/configuracoes/clientes/criar')}
              >
                Novo cliente
              </Button>
            )}
          </Cartao>
        )}
      </Box>
    </Flex>
  );
}
